/**
 * Enum presentation: turning `PARTIALLY_RECEIVED` into "Partially Received" with a
 * semantic colour, for every enum in the system.
 *
 * A per-enum lookup table would be ~400 rows and would rot. Instead labels come
 * from a humaniser plus an acronym dictionary, and tones come from ordered keyword
 * rules. Both accept explicit overrides for the handful of genuinely special cases.
 */

export type StatusTone =
  | 'neutral' // not started, informational
  | 'info' // in flight, no action needed
  | 'progress' // actively being worked
  | 'success' // terminal, good
  | 'warning' // needs attention
  | 'danger' // failed, rejected, terminal bad
  | 'muted'; // dormant, cancelled, not applicable

/** Words that must not be title-cased naively. */
const ACRONYMS: Record<string, string> = {
  DCR: 'DCR',
  DISCOM: 'DISCOM',
  FTR: 'FTR',
  QC: 'QC',
  GST: 'GST',
  IGST: 'IGST',
  CGST: 'CGST',
  SGST: 'SGST',
  LT: 'LT',
  HT: 'HT',
  DD: 'DD',
  NEFT: 'NEFT',
  RTGS: 'RTGS',
  IMPS: 'IMPS',
  UPI: 'UPI',
  ACDB: 'ACDB',
  DCDB: 'DCDB',
  KW: 'kW',
  WP: 'Wp',
  NOS: 'Nos',
  MTR: 'Mtr',
  KG: 'Kg',
  LTR: 'Ltr',
  EMD: 'EMD',
  PAN: 'PAN',
  IFSC: 'IFSC',
  GPS: 'GPS',
  HR: 'HR',
  UAN: 'UAN',
  ESIC: 'ESIC',
  PF: 'PF',
  CTC: 'CTC',
};

/** Whole-value overrides where the humanised form reads badly. */
const LABEL_OVERRIDES: Record<string, string> = {
  ON_GRID: 'On-Grid',
  OFF_GRID: 'Off-Grid',
  SINGLE_PHASE: 'Single Phase',
  THREE_PHASE: 'Three Phase',
  NON_DCR: 'Non-DCR',
  NOT_APPLICABLE: 'Not Applicable',
  QC_PENDING: 'QC Pending',
  QC_PASSED: 'QC Passed',
  QC_FAILED: 'QC Failed',
  LOAN_DISBURSEMENT: 'Loan Disbursement',
  SUBSIDY_CREDIT: 'Subsidy Credit',
  NIL_RATED: 'Nil Rated',
  ZERO_RATED: 'Zero Rated',
  CGST_SGST: 'CGST + SGST',
  WRITTEN_OFF: 'Written Off',
  PM_SURYA_GHAR: 'PM Surya Ghar',
  HALF_DAY: 'Half Day',
  FULL_DAY: 'Full Day',
  FIRST_HALF: 'First Half',
  SECOND_HALF: 'Second Half',
  WEEKLY_OFF: 'Weekly Off',
  ON_NOTICE: 'On Notice',
  ON_LEAVE: 'On Leave',
  DAILY_WAGE: 'Daily Wage',
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  PERCENT_OF_BASIC: '% of Basic',
  PERCENT_OF_GROSS: '% of Gross',
};

/**
 * Convert a SCREAMING_SNAKE enum value to a human label.
 * `PARTIALLY_RECEIVED` → `Partially Received`; `NON_DCR` → `Non-DCR`.
 */
export const humanizeEnum = (value: string | null | undefined): string => {
  if (!value) return '—';
  const override = LABEL_OVERRIDES[value];
  if (override) return override;

  return value
    .split('_')
    .map((word) => {
      const acronym = ACRONYMS[word];
      if (acronym) return acronym;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/** Ordered rules — first match wins, so put terminal states before generic ones. */
const TONE_RULES: [tone: StatusTone, values: readonly string[]][] = [
  [
    'danger',
    [
      'REJECTED',
      'FAILED',
      'QC_FAILED',
      'LOST',
      'DISABLED',
      'BLACKLISTED',
      'NON_COMPLIANT',
      'DAMAGED',
      'DAMAGE',
      'ERROR',
      'OVERDUE',
      'ESCALATED',
      'SCRAP',
      'SCRAPPED',
      'ABSENT',
      'ABSCONDED',
      'TERMINATED',
    ],
  ],
  [
    'success',
    [
      'ACTIVE',
      'APPROVED',
      'COMPLETED',
      'COMMISSIONED',
      'PAID',
      'DISBURSED',
      'WON',
      'DELIVERED',
      'RECEIVED',
      'POSTED',
      'VERIFIED',
      'CONVERTED',
      'QC_PASSED',
      'FEASIBILITY_APPROVED',
      'TECHNICAL_QUALIFIED',
      'INSTALLED',
      'SUCCESS',
      'DCR',
      'PRESENT',
      'FINALISED',
      'ACCEPTED',
      'SYNCED',
      'CLOSED',
    ],
  ],
  [
    'warning',
    [
      'PENDING_APPROVAL',
      'PENDING_VERIFICATION',
      'QUERY_RAISED',
      'ON_HOLD',
      'WAITING_FOR_PARTS',
      'QC_PENDING',
      'AGREEMENT_PENDING',
      'PARTIALLY_PAID',
      'PARTIALLY_RECEIVED',
      'PARTIALLY_DELIVERED',
      'PARTIALLY_PARSED',
      'PARTIAL',
      'DUE',
      'BLOCKED',
      'SUSPENDED',
      'RETURNED',
      'RETURN_IN',
      'RETURN_OUT',
      'PROMISED',
      'WARNING',
      'REVISED',
      'EXPIRED',
      'LATE',
      'HALF_DAY',
      'ON_NOTICE',
      'PROBATION',
    ],
  ],
  [
    'progress',
    [
      'IN_PROGRESS',
      'IN_TRANSIT',
      'INSTALLATION',
      'PICKING',
      'PACKED',
      'PREPARING',
      'NEGOTIATION',
      'UNDER_REVIEW',
      'UNDER_VERIFICATION',
      'INSPECTION_DONE',
      'INSPECTION_SCHEDULED',
      'VISIT_SCHEDULED',
      'METER_REQUESTED',
      'METER_INSTALLED',
      'AGREEMENT_SIGNED',
      'MATERIAL_PROCUREMENT',
      'DISPATCH',
      'DISPATCHED',
      'DISCOM_APPROVAL',
      'COMMISSIONING',
      'SUBSIDY_PROCESSING',
      'SURVEY_DONE',
      'BANK_DETAILS_SUBMITTED',
      'FINANCIAL_OPENED',
      'ALLOCATED',
    ],
  ],
  [
    'info',
    [
      'NEW',
      'SUBMITTED',
      'SENT',
      'SCHEDULED',
      'RESCHEDULED',
      'SURVEY_SCHEDULED',
      'CUSTOMER_CONFIRMATION',
      'QUOTED',
      'CONTACTED',
      'QUALIFIED',
      'REGISTERED',
      'APPLICATION_SUBMITTED',
      'PLANNED',
      'PLANNING',
      'IDENTIFIED',
      'DOCUMENT_PURCHASED',
      'UPLOADED',
      'PARSED',
      'INVITED',
      'OPEN',
      'INFO',
      'LEAVE',
      'ON_LEAVE',
      'IN_STOCK',
      'PENDING_REGISTRATION',
    ],
  ],
  [
    'muted',
    [
      'CANCELLED',
      'DROPPED',
      'WITHDRAWN',
      'INACTIVE',
      'SKIPPED',
      'NOT_APPLICABLE',
      'NOT_DUE',
      'NON_DCR',
      'EXEMPT',
      'NIL_RATED',
      'ZERO_RATED',
      'REFUNDED',
      'HOLIDAY',
      'WEEKLY_OFF',
      'RESIGNED',
      'RETIRED',
      'SUPERSEDED',
    ],
  ],
  ['neutral', ['DRAFT', 'PENDING', 'NOT_STARTED', 'NOT_SYNCED']],
];

const toneIndex = new Map<string, StatusTone>();
for (const [tone, values] of TONE_RULES) {
  for (const value of values) {
    if (!toneIndex.has(value)) toneIndex.set(value, tone);
  }
}

/** Domain-specific overrides where the same word means different things. */
const TONE_OVERRIDES: Record<string, StatusTone> = {
  // A lead sitting in NEW is fine; an unread payment follow-up is not.
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'neutral',
};

export const statusTone = (value: string | null | undefined): StatusTone => {
  if (!value) return 'neutral';
  return TONE_OVERRIDES[value] ?? toneIndex.get(value) ?? 'neutral';
};

export interface StatusPresentation {
  value: string;
  label: string;
  tone: StatusTone;
}

export const describeStatus = (value: string | null | undefined): StatusPresentation => ({
  value: value ?? '',
  label: humanizeEnum(value),
  tone: statusTone(value),
});

/** Build `{ value, label }` option lists for selects and filter panels. */
export const toOptions = <T extends string>(
  values: readonly T[],
): { value: T; label: string }[] => values.map((value) => ({ value, label: humanizeEnum(value) }));
